import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { BreadcrumbSchema } from '../components/StructuredData';
import RegistrationForm from '../components/RegistrationForm';
import { registerUser } from '../services/authService';

const RegistrationPage: React.FC = () => {
  const [status, setStatus] = useState<{
    type: 'success' | 'pending' | 'error';
    message: string;
  } | null>(null);

  useEffect(() => {
    if (status?.type === 'success') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [status?.type]);

  const handleRegister = async (userData: {
    name: string;
    email: string;
    company: string;
    street: string;
    city: string;
    postalCode: string;
    password: string;
    voucher?: string;
    agbAccepted: boolean;
  }) => {
    try {
      setStatus(null);
      setStatus({
        type: 'pending',
        message: 'Registrierung wird verarbeitet...'
      });

      window.scrollTo({ top: 0, behavior: 'smooth' });

      if (userData.voucher && userData.voucher.trim()) {
        localStorage.setItem('activeVoucher', userData.voucher.trim());
      } else {
        localStorage.removeItem('activeVoucher');
      }

      try {
        await registerUser(userData);
        setStatus({
          type: 'success',
          message: 'Bitte bestätigen Sie Ihre E-Mail-Adresse. Danach werden Sie automatisch zur Zahlung weitergeleitet.'
        });
      } catch (registerError: any) {
        console.error('Registration error details:', registerError);
        localStorage.removeItem('activeVoucher');
        setStatus({
          type: 'error',
          message: registerError.message || 'Bei der Registrierung ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.'
        });
      }
    } catch (error) {
      console.error('Registration error:', error);
      localStorage.removeItem('activeVoucher');
      setStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.'
      });
    }
  };

  return (
    <>
      <SEOHead
        title="Registrierung - Jetzt starten"
        description="Registrieren Sie sich jetzt für den EU AI Act Kompetenznachweis. Werden Sie in 45 Minuten compliant und erhalten Sie Ihr Zertifikat."
        keywords={['Registrierung', 'Anmeldung', 'EU AI Act Kurs buchen', 'KI-Schulung anmelden']}
        canonicalUrl="/registrieren"
      />
      <BreadcrumbSchema items={[
        { name: 'Startseite', url: '/' },
        { name: 'Registrierung', url: '/registrieren' }
      ]} />
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          {status?.type === 'success' ? (
            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>

                <h1 className="text-2xl font-bold text-gray-800 mb-4">
                  Registrierung erfolgreich!
                </h1>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                  <p className="text-blue-800 font-medium mb-2">
                    Bestätigen Sie Ihre E-Mail-Adresse
                  </p>
                  <p className="text-blue-700 text-sm">
                    {status.message}
                  </p>
                </div>

                <div className="space-y-4">
                  <p className="text-gray-600">
                    Prüfen Sie Ihren Posteingang und Spam-Ordner. Nach der Bestätigung werden Sie automatisch zur Zahlung weitergeleitet.
                  </p>

                  <Link
                    to="/login"
                    className="inline-block bg-[#2574B5] hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
                  >
                    Zurück zum Login
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">
                EU AI Act Kompetenznachweis
              </h1>

              <RegistrationForm onRegister={handleRegister} />

              {status && status.type !== 'success' && (
                <div className={`max-w-2xl mx-auto mt-8 p-4 rounded-lg ${
                  status.type === 'pending' ? 'bg-blue-50 text-blue-800 border border-blue-200' :
                  'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  <p className="text-center font-medium">{status.message}</p>
                </div>
              )}

              <div className="mt-8 text-center">
                <p className="text-gray-600">
                  Bereits registriert? <Link to="/login" className="text-blue-600 hover:underline">Zum Login</Link>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default RegistrationPage;
